import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  Shield,
  FileText,
  Lock,
  Upload,
  FolderOpen,
  Eye,
  Clock,
  Mail,
  DollarSign,
  Users,
  Zap,
  Server,
  X,
} from "lucide-react";
import LandingPageLayout from "../../layout/LandingPageLayout";
import PageMeta from "../../components/common/common/PageMeta";

const LandingPage: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<{
    src: string;
    alt: string;
  } | null>(null);

  return (
    <LandingPageLayout>
      <PageMeta
        title="Secure File Sharing for Small Business | Casekeep"
        description="Secure file sharing for small business with the highest security standards. Collect sensitive documents via magic links with no client logins required. E-signatures and encrypted storage included."
      />
      {/* Beta Banner */}
      <div className="bg-brand-600 dark:bg-brand-700">
        <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
            <p className="text-white font-medium">
              <span className="hidden sm:inline">
                Casekeep Beta is Live! Join our beta securely request
                information from clients for free.
              </span>
              <span className="sm:hidden">Casekeep Beta is Live!</span>
            </p>
            <a
              href="mailto:hello@casekeep.app?subject=Beta Access Request"
              className="inline-flex items-center px-4 py-1.5 text-sm font-medium text-brand-600 bg-white rounded-full hover:bg-brand-50 transition-colors"
            >
              Request an invitation
            </a>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-accent from-white to-gray-50 dark:from-gray-900 dark:to-gray-950 py-20 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
              Your inbox is not the place for{" "}
              <span className="text-brand-600">
                W-2s and Social Security numbers.
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-10 leading-relaxed">
              Collect and share sensitive information with magic links, set
              automated reminders, and collect e-signatures all in one
              easy-to-use platform.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors shadow-lg hover:shadow-xl w-full sm:w-auto"
              >
                Join Beta
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-brand-700 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors w-full sm:w-auto"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Product Showcase Section */}
      <section className="py-20 bg-white dark:bg-gray-900 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              See how Casekeep is different
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Send and receive secure information for clients. E-signatures and
              file sharing? We do that too.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {/* Case Records Screenshot */}
            <div className="group relative">
              <button
                onClick={() =>
                  setSelectedImage({
                    src: "/case-records.png",
                    alt: "Case records management interface",
                  })
                }
                className="relative rounded-xl overflow-hidden shadow-theme-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-transform duration-300 group-hover:-translate-y-2 group-hover:shadow-xl cursor-pointer w-full"
              >
                <img
                  src="/case-records.png"
                  alt="Case records management interface"
                  className="w-full h-auto"
                />
              </button>
              <div className="mt-4 text-center">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Case Management
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Organize all client documents in one place
                </p>
              </div>
            </div>

            {/* Signature Template Screenshot */}
            <div className="group relative">
              <button
                onClick={() =>
                  setSelectedImage({
                    src: "e-signature.png",
                    alt: "Signature template interface",
                  })
                }
                className="relative rounded-xl overflow-hidden shadow-theme-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-transform duration-300 group-hover:-translate-y-2 group-hover:shadow-xl cursor-pointer w-full"
              >
                <img
                  src="/e-signature.png"
                  alt="Signature template interface"
                  className="w-full h-auto"
                />
              </button>
              <div className="mt-4 text-center">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  E-Signatures
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Get documents signed with full audit trails
                </p>
              </div>
            </div>

            {/* Form Request Screenshot */}
            <div className="group relative">
              <button
                onClick={() =>
                  setSelectedImage({
                    src: "/request-intake.png",
                    alt: "Form request interface",
                  })
                }
                className="relative rounded-xl overflow-hidden shadow-theme-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-transform duration-300 group-hover:-translate-y-2 group-hover:shadow-xl cursor-pointer w-full"
              >
                <img
                  src="/request-intake.png"
                  alt="Form request interface"
                  className="w-full h-auto"
                />
              </button>
              <div className="mt-4 text-center">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Information Requests
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Collect client information with no login required
                </p>
              </div>
            </div>

            {/* Audit Logs Screenshot */}
            <div className="group relative">
              <button
                onClick={() =>
                  setSelectedImage({
                    src: "/audit-logs.png",
                    alt: "Audit logs interface",
                  })
                }
                className="relative rounded-xl overflow-hidden shadow-theme-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-transform duration-300 group-hover:-translate-y-2 group-hover:shadow-xl cursor-pointer w-full"
              >
                <img
                  src="/audit-logs.png"
                  alt="Audit logs interface"
                  className="w-full h-auto"
                />
              </button>
              <div className="mt-4 text-center">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Audit Logging
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Track all activity with detailed audit trails
                </p>
              </div>
            </div>

            {/* File Sharing Screenshot */}
            <div className="group relative">
              <button
                onClick={() =>
                  setSelectedImage({
                    src: "/file-sharing.png",
                    alt: "File sharing interface",
                  })
                }
                className="relative rounded-xl overflow-hidden shadow-theme-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-transform duration-300 group-hover:-translate-y-2 group-hover:shadow-xl cursor-pointer w-full"
              >
                <img
                  src="/file-sharing.png"
                  alt="File sharing interface"
                  className="w-full h-auto"
                />
              </button>
              <div className="mt-4 text-center">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  File Sharing
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Send secure files with expiring or self-destructing links
                </p>
              </div>
            </div>

            {/* Client Portal Screenshot */}
            <div className="group relative">
              <button
                onClick={() =>
                  setSelectedImage({
                    src: "/branded-client-portal.png",
                    alt: "Branded client portal interface",
                  })
                }
                className="relative rounded-xl overflow-hidden shadow-theme-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-transform duration-300 group-hover:-translate-y-2 group-hover:shadow-xl cursor-pointer w-full"
              >
                <img
                  src="/branded-client-portal.png"
                  alt="Branded client portal interface"
                  className="w-full h-auto"
                />
              </button>
              <div className="mt-4 text-center">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Client Portal
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Provide clients with a secure, branded dashboard
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-brand-600 to-brand-700 dark:from-brand-700 dark:to-brand-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Built for small but mighty teams 💪
          </h2>
          <p className="text-lg sm:text-xl text-brand-100 leading-relaxed mb-6">
            You may not have big budgets or armies of staff, but you bring
            drive, creativity, and ingenuity to every project. Casekeep helps
            you stay on top of your work without adding unnecessary complexity.
          </p>
          <p className="text-lg sm:text-xl text-brand-100 leading-relaxed">
            With{" "}
            <span className="font-bold">
              predictable pricing and no surprise costs
            </span>
            , you can focus on what matters most — serving your clients and
            moving your work forward — while we handle the secure sharing and
            organization behind the scenes.
          </p>
        </div>
      </section>

      {/* Security-First Features Section */}
      <section className="py-20 dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Your clients' information, protected from start to finish
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Unlike standard file storage solutions, Casekeep is built for
              professionals handling sensitive client information.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:shadow-theme-lg transition-shadow">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                End-to-end encryption
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                All files and form submissions are encrypted both in transit and
                at rest — your clients' sensitive data stays protected.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:shadow-theme-lg transition-shadow">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Encrypted signature envelopes
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Every envelope is fully encrypted with complete audit trails —
                built for compliance and peace of mind.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:shadow-theme-lg transition-shadow">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Audit logs & access history
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Full transparency for each case. Track who accessed what and
                when — perfect for compliance and peace of mind.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:shadow-theme-lg transition-shadow">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Expiring secure links
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Create temporary access to sensitive files with self-destruct
                options — links expire automatically when you need them to.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:shadow-theme-lg transition-shadow">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Email verification for access
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Add an extra layer of security for critical documents by
                requiring email verification before link access.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:shadow-theme-lg transition-shadow">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Legally compliant & trusted
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Built for professionals in tax, legal, and financial services
                who need secure, auditable, and compliant workflows.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/security"
              className="inline-flex items-center text-brand-600 dark:text-brand-400 font-medium hover:underline"
            >
              See our full security practices
              <span className="ml-1">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Case-Based Organization Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Case-based organization — not just folders
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                Most cloud storage tools are just folders. Casekeep gives you a
                clear workflow and context around sensitive information.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FolderOpen className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Every client gets a "case"
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Centralize all documents, forms, and signatures in one
                      organized place — no more hunting through email threads.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Flexible folder structure
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Mix files and form submissions inside cases using logical
                      folder structures that make sense for your workflow.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Upload className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Form submissions linked to cases
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Structured collection of client data, not just raw files —
                      everything stays organized and contextual.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-brand-50 to-brand-100 dark:from-brand-950 dark:to-brand-900 rounded-2xl p-8 border border-brand-200 dark:border-brand-800">
              <div className="bg-white dark:bg-gray-900 rounded-lg p-6 shadow-theme-md mb-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-brand-100 dark:bg-brand-950 rounded flex items-center justify-center">
                    <FolderOpen className="w-5 h-5 text-brand-600 dark:text-brand-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      Smith Family Tax Return 2025
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Created Jan 15, 2025
                    </p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <FileText className="w-4 h-4" />
                    <span>W2-Forms.pdf</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <FileText className="w-4 h-4" />
                    <span>1099-MISC.pdf</span>
                  </div>
                  <div className="flex items-center gap-2 text-brand-600 dark:text-brand-400">
                    <Check className="w-4 h-4" />
                    <span>Tax Organizer Form — Submitted</span>
                  </div>
                  <div className="flex items-center gap-2 text-brand-600 dark:text-brand-400">
                    <Check className="w-4 h-4" />
                    <span>Engagement Letter — Signed</span>
                  </div>
                </div>
              </div>
              <p className="text-center text-sm text-gray-700 dark:text-gray-300 italic">
                Everything in context — no more scattered files
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Streamlined Client Interaction Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Streamline your client interaction
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              No more juggling emails, links, and shared drives. Make it
              painless for clients to work with you.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 text-center hover:shadow-theme-lg transition-shadow">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Flexibility for your clients
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Clients upload files, fill forms, and sign documents via secure
                links OR your custom client portal
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 text-center hover:shadow-theme-lg transition-shadow">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Quick form creation per case
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Generate a link to collect client info in seconds — no extra
                setup or configuration required.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 text-center hover:shadow-theme-lg transition-shadow">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Secure, painless document requests
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Reduce friction while maintaining privacy — clients get a
                simple, secure experience every time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Predictability Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Prices that grow with your work, not your team.
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Only pay for what matters — storage and usage, no per-user fees.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-200 dark:border-gray-700 shadow-theme-sm">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Predictable base pricing
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Covers storage and infrastructure — nothing hidden, no surprise
                fees.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span>No per-user pricing</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span>Your whole team can access the system</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span>Costs don't increase with headcount</span>
                </li>
              </ul>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-gray-200 dark:border-gray-700 shadow-theme-sm">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Signature envelopes included
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Each plan includes a yearly envelope allowance designed to cover
                normal use. Need more? Extra envelopes are available at a low,
                predictable rate.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span>Base quota included in every plan</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span>
                    Extra envelopes at a fair price whenever you need them
                  </span>
                </li>
                <li className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                  <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                  <span>No forced bundles, no surprises</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Productivity & Workflow Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Productivity & workflow benefits
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Replace multiple tools with one system — reduce operational
              friction while increasing oversight and accountability.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 dark:bg-gray-950 rounded-xl p-6 border border-gray-200 dark:border-gray-800">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mb-4">
                <Server className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                All-in-one system
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Combine storage, forms, and signatures in one place — replaces
                Dropbox + DocuSign + spreadsheets.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-950 rounded-xl p-6 border border-gray-200 dark:border-gray-800">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Complete audit trail
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Peace of mind for compliance and reporting — every action is
                logged and traceable.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-950 rounded-xl p-6 border border-gray-200 dark:border-gray-800">
              <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Scales with your team
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Supports large teams and high-volume firms — tiers scale up to
                multiple terabytes of storage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing section hidden for beta - preserved for future use */}
      {false && (
        <section className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Simple pricing that scales with your practice
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                No per-seat pricing. No forced signature bundles. Pay for what
                you store — and only pay for signatures when you use them.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Solo Plan */}
              <div className="flex flex-col bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-8 hover:border-brand-500 dark:hover:border-brand-600 transition-all shadow-theme-sm hover:shadow-theme-lg">
                <div className="mb-6">
                  <div className="text-2xl mb-2">🌱</div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Solo
                  </h3>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      $39
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      / month
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    billed annually
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    $50 / month (monthly)
                  </p>
                </div>

                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Built for independent professionals who want secure,
                  professional client workflows without complexity.
                </p>

                <div className="flex-grow">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                    What's included
                  </h4>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        500 GB secure storage
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        End-to-end encrypted file uploads & downloads
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Secure client portals
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Case-based organization
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Secure share links
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Audit logs & access history
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Email-based client access (no accounts required)
                      </span>
                    </li>
                  </ul>

                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-2">
                      E-signatures
                    </h5>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                      $1.50{" "}
                      <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                        per envelope
                      </span>
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      No monthly minimums • No workflow interruptions
                    </p>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                    Best for: solo practitioners, tax preparers, consultants,
                    therapists, bookkeepers
                  </p>
                </div>

                <Link
                  to="/signup"
                  className="mt-6 inline-flex items-center justify-center px-6 py-3 text-base font-medium text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-950 rounded-lg hover:bg-brand-100 dark:hover:bg-brand-900 transition-colors"
                >
                  Get Started
                </Link>
              </div>

              {/* Pro Plan */}
              <div className="flex flex-col bg-white dark:bg-gray-800 border-2 border-brand-500 dark:border-brand-600 rounded-2xl p-8 shadow-theme-lg relative">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Popular
                </div>

                <div className="mb-6">
                  <div className="text-2xl mb-2">🚀</div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Pro
                  </h3>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      $99
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      / month
                    </span>
                  </div>
                </div>

                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  For growing practices managing more clients, more documents,
                  and higher volume.
                </p>

                <div className="flex-grow">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                    Everything in Solo, plus
                  </h4>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        1 TB secure storage
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Higher file size limits
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Faster bulk uploads & downloads
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Advanced case organization tools
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Priority support
                      </span>
                    </li>
                  </ul>

                  <div className="bg-brand-50 dark:bg-brand-950 rounded-lg p-4 mb-6">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-2">
                      E-signatures
                    </h5>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                      $1.00{" "}
                      <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                        per envelope
                      </span>
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Usage-based pricing — scales with your work
                    </p>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                    Best for: small firms, multi-partner practices, growing
                    client bases
                  </p>
                </div>

                <Link
                  to="/signup"
                  className="mt-6 inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors shadow-sm"
                >
                  Get Started
                </Link>
              </div>

              {/* Firm Plan */}
              <div className="flex flex-col bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl p-8 hover:border-brand-500 dark:hover:border-brand-600 transition-all shadow-theme-sm hover:shadow-theme-lg">
                <div className="mb-6">
                  <div className="text-2xl mb-2">🏢</div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Firm
                  </h3>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      $299
                    </span>
                    <span className="text-gray-600 dark:text-gray-400">
                      / month
                    </span>
                  </div>
                </div>

                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Built for high-volume firms that need predictable
                  infrastructure, not per-user chaos.
                </p>

                <div className="flex-grow">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                    Everything in Pro, plus
                  </h4>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        5 TB secure storage
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Designed for high-volume client intake
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Optimized for large document sets
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Firm-wide case workflows
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        Dedicated support channel
                      </span>
                    </li>
                  </ul>

                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-2">
                      E-signatures
                    </h5>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                      $1.00{" "}
                      <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                        per envelope
                      </span>
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Ideal for firms sending hundreds or thousands per year
                    </p>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                    Best for: accounting firms, legal firms, financial services
                    teams
                  </p>
                </div>

                <Link
                  to="/signup"
                  className="mt-6 inline-flex items-center justify-center px-6 py-3 text-base font-medium text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-950 rounded-lg hover:bg-brand-100 dark:hover:bg-brand-900 transition-colors"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Beta Access Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
            Free During Beta
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Casekeep is currently in invite-only beta. Beta users get free
            access to all features with a limit of 5 signature envelopes.
          </p>

          <div className="bg-brand-50 dark:bg-brand-950 rounded-2xl p-8 border border-brand-200 dark:border-brand-800">
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-600 dark:text-brand-400">
                  Free
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  During Beta
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-600 dark:text-brand-400">
                  5
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Signature Envelopes
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-600 dark:text-brand-400">
                  200MB
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Storage
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Full pricing will be available when we launch publicly.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
              >
                Join with Beta Code
              </Link>
              <a
                href="mailto:hello@casekeep.app?subject=Beta Access Request"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-brand-700 dark:text-brand-400 bg-white dark:bg-gray-800 rounded-lg border border-brand-300 dark:border-brand-700 hover:bg-brand-50 dark:hover:bg-brand-900 transition-colors"
              >
                Request Beta Access
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Signatures Section */}
      <section className="py-20 bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Signatures without extra costs
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Most e-signature tools charge for every user—even for clients who
            aren't employees. With Casekeep, you only pay for what you actually
            use.
          </p>
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-gray-800 shadow-theme-md">
            <p className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              Every plan comes with a base number of signature envelopes:
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              <div>
                <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-brand-600 dark:text-brand-400" />
                </div>
                <p className="font-medium text-gray-900 dark:text-white">
                  A generous annual envelope count included.
                </p>
              </div>
              <div>
                <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-brand-600 dark:text-brand-400" />
                </div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Extra envelopes are affordable. Only pay for what you need.
                </p>
              </div>
              <div>
                <div className="w-12 h-12 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-brand-600 dark:text-brand-400" />
                </div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Predictable costs. You're always in control.
                </p>
              </div>
            </div>
            <p className="mt-8 text-gray-600 dark:text-gray-400">
              No surprise fees. No forced upgrades. Just a simple, fair way to
              manage signatures.
            </p>
          </div>
        </div>
      </section>

      {/* One Platform Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            One platform instead of three
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-12 text-center">
            Casekeep replaces multiple subscriptions with a single, unified
            system.
          </p>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="flex items-start gap-4 p-6 bg-gray-50 dark:bg-gray-950 rounded-xl border border-gray-200 dark:border-gray-800">
              <div className="w-10 h-10 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center flex-shrink-0">
                <FolderOpen className="w-5 h-5 text-brand-600 dark:text-brand-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                  File storage tools
                </h3>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 dark:bg-gray-950 rounded-xl border border-gray-200 dark:border-gray-800">
              <div className="w-10 h-10 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center flex-shrink-0">
                <Upload className="w-5 h-5 text-brand-600 dark:text-brand-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                  Secure upload links
                </h3>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 dark:bg-gray-950 rounded-xl border border-gray-200 dark:border-gray-800">
              <div className="w-10 h-10 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-brand-600 dark:text-brand-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                  Client portals
                </h3>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 dark:bg-gray-950 rounded-xl border border-gray-200 dark:border-gray-800">
              <div className="w-10 h-10 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-brand-600 dark:text-brand-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                  Signature tools
                </h3>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-gray-50 dark:bg-gray-950 rounded-xl border border-gray-200 dark:border-gray-800">
              <div className="w-10 h-10 bg-brand-100 dark:bg-brand-950 rounded-lg flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-brand-600 dark:text-brand-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                  Case organization spreadsheets
                </h3>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-600 dark:text-gray-400 mt-8">
            All without stitching together multiple subscriptions.
          </p>
        </div>
      </section>

      {/* Built for Sensitive Work Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Built for sensitive work
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-12 text-center">
            Casekeep is designed for professionals handling:
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-12">
            {[
              "Tax documents",
              "Legal paperwork",
              "Financial records",
              "Identity documents",
              "Client intake forms",
            ].map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800"
              >
                <Check className="w-5 h-5 text-brand-600 dark:text-brand-400 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300">{item}</span>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-xl text-gray-900 dark:text-white font-medium">
              Security, clarity, and trust come first.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-brand-600 dark:bg-brand-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to simplify your client workflows?
          </h2>
          <p className="text-lg text-brand-100 mb-4">
            Join our invite-only beta. Have a code? Get started in minutes.
          </p>
          <p className="text-sm text-brand-200 mb-8">
            Beta accounts include 5 free signature envelopes and never expire.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-brand-600 bg-white rounded-lg hover:bg-gray-50 transition-colors shadow-lg"
            >
              Join with Beta Code
            </Link>
            <a
              href="mailto:hello@casekeep.app?subject=Beta Access Request"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white border-2 border-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              Request Access
            </a>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white transition-colors"
            aria-label="Close modal"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={selectedImage.src}
            alt={selectedImage.alt}
            className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </LandingPageLayout>
  );
};

export default LandingPage;
